import { AxiosInstance } from 'axios';
import { CalendarEvent, ScheduleCalendar } from '../types';

/**
 * Calendar context for the schedule view.
 *
 * Read-only: the overlay shows where a day is already committed so tasks can be
 * placed around it. Scheduling a task never writes back to a calendar.
 */
export class ScheduleApi {
  constructor(private client: AxiosInstance) {}

  /**
   * Events across the board's calendar-capable connections, for a window.
   *
   * @param from Inclusive start of the window.
   * @param to Exclusive end of the window. The region rejects a window longer
   *   than a month.
   * @param excludeConnectionIds Calendars hidden in the overlay. The region
   *   does not fetch them at all, so hiding one also saves its provider call.
   *   Sent as a repeated `excludeConnectionIds` parameter; ids the region does
   *   not recognise are ignored there.
   */
  async getEvents(
    boardId: string,
    from: Date,
    to: Date,
    excludeConnectionIds: readonly string[] = [],
  ): Promise<CalendarEvent[]> {
    const excluded = excludeConnectionIds
      .map((id) => `&excludeConnectionIds=${encodeURIComponent(id)}`)
      .join('');

    const url =
      `/api/boards/${encodeURIComponent(boardId)}/schedule/events` +
      `?from=${encodeURIComponent(from.toISOString())}` +
      `&to=${encodeURIComponent(to.toISOString())}` +
      excluded;

    const response = await this.client.get<CalendarEvent[]>(url);

    // Dates deserialize as ISO strings; the grid does arithmetic on them.
    return response.data.map((event) => ({
      ...event,
      start: new Date(event.start),
      end: new Date(event.end),
    }));
  }

  /**
   * The board's calendar connections, one overlay toggle each. Never calls a
   * provider, so it is cheap enough to fetch whether or not the overlay is on.
   */
  async getCalendars(boardId: string): Promise<ScheduleCalendar[]> {
    const response = await this.client.get<ScheduleCalendar[]>(
      `/api/boards/${encodeURIComponent(boardId)}/schedule/calendars`,
    );

    return response.data;
  }
}
